import React, { useCallback, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  Paper,
  styled,
  Alert,
  CircularProgress,
} from "@mui/material";
import { Upload, File, X } from "lucide-react";
import { useDropzone } from "react-dropzone";

const DropZoneWrapper = styled(Paper)(({ theme }) => ({
  border: "2px dashed",
  borderColor: theme.palette.divider,
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  display: "flex",
  flexDirection: "column",
  alignItems: "center",
  justifyContent: "center",
  textAlign: "center",
  transition: "all 0.2s ease-in-out",
  "&:hover": {
    backgroundColor: theme.palette.grey[100],
    borderColor: theme.palette.primary.light,
  },
}));

interface ContractUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onPreview: (files: File[]) => void;
  isUploading: boolean;
  "aria-labelledby"?: string;
}

const ContractUploadDialog: React.FC<ContractUploadDialogProps> = (props) => {
  const { open, onClose, onPreview, isUploading } = props;
  const [files, setFiles] = useState<File[]>([]);
  const [error, setError] = useState<string | null>(null);

  // 3 MB in bytes
  const MAX_FILE_SIZE = 3 * 1024 * 1024;

  const formatFileSize = (bytes: number): string => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const onDrop = useCallback(
    (acceptedFiles: File[], rejectedFiles: any[]) => {
      setError(null);

      if (rejectedFiles.length > 0) {
        const sizeErrors = rejectedFiles.filter((file) =>
          file.errors?.some((error: any) => error.code === "file-too-large")
        );

        if (sizeErrors.length > 0) {
          setError(
            `File size exceeds 3 MB limit. Maximum file size allowed is 3 MB.`
          );
          return;
        }
      }

      if (acceptedFiles.length > 3) {
        setError("Maximum 3 files allowed");
        return;
      }

      const oversizedFiles = acceptedFiles.filter(
        (file) => file.size > MAX_FILE_SIZE
      );
      if (oversizedFiles.length > 0) {
        setError(
          `The following files exceed the 3 MB limit: ${oversizedFiles
            .map((f) => `${f.name} (${formatFileSize(f.size)})`)
            .join(", ")}`
        );
        return;
      }

      setFiles(acceptedFiles);
    },
    [MAX_FILE_SIZE]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      "application/pdf": [".pdf"],
      "image/*": [".png", ".jpg", ".jpeg"],
    },
    maxFiles: 3,
    maxSize: MAX_FILE_SIZE, // 3 MB limit
  });

  const handleRemoveFile = (index: number) => {
    const newFiles = [...files];
    newFiles.splice(index, 1);
    setFiles(newFiles);
  };

  const handlePreview = () => {
    if (files.length === 0) {
      setError("Please select at least one file");
      return;
    }
    onPreview(files);
    setFiles([]);
  };

  const handleClose = () => {
    setFiles([]);
    setError(null);
    onClose();
  };

  return (
    <Dialog
      open={open}
      maxWidth="sm"
      fullWidth
      aria-labelledby={
        props["aria-labelledby"] || "contract-upload-dialog-title"
      }
    >
      <DialogTitle id="contract-upload-dialog-title">
        Upload Contract Documents
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        <DropZoneWrapper
          {...getRootProps()}
          sx={{
            transition: "all 0.2s ease-in-out",
            "&:hover": {
              borderColor: "primary.light",
              backgroundColor: "rgba(0, 0, 0, 0.01)",
            },
          }}
        >
          <input {...getInputProps()} aria-label="Upload contract documents" />
          {isDragActive ? (
            <Typography variant="body1">Drop the files here...</Typography>
          ) : (
            <>
              <Upload
                size={48}
                style={{ marginBottom: 16, color: "#6c757d" }}
              />
              <Typography variant="body1" sx={{ mb: 1, fontWeight: "medium" }}>
                Drag and drop HUD/ALTA/CD documents here
              </Typography>
              <Typography
                variant="body2"
                sx={{ mb: 2, color: "text.secondary" }}
              >
                Supported formats: PDF, PNG, JPG (Max 3 files, 3 MB each)
              </Typography>
              <Button
                variant="outlined"
                color="primary"
                aria-label="Select contract files to upload"
              >
                Select Files
              </Button>
            </>
          )}
        </DropZoneWrapper>

        {files.length > 0 && (
          <Box sx={{ mt: 3 }}>
            <Typography variant="subtitle2" sx={{ mb: 1 }}>
              Selected files:
            </Typography>
            {files.map((file, index) => (
              <Box
                key={file.name}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  p: 1,
                  borderBottom: "1px solid",
                  borderColor: "divider",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <File size={16} />
                  <Typography variant="body2">
                    {file.name} ({formatFileSize(file.size)})
                  </Typography>
                </Box>
                <Button
                  size="small"
                  color="error"
                  onClick={() => handleRemoveFile(index)}
                >
                  <X size={16} />
                </Button>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={handleClose} disabled={isUploading} sx={{ mr: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handlePreview}
          variant="contained"
          disabled={isUploading || files.length === 0}
          startIcon={isUploading ? <CircularProgress size={16} /> : null}
          aria-busy={isUploading}
        >
          {isUploading ? "Processing..." : "Preview"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ContractUploadDialog;
